import { useEffect, useRef } from 'react';

enum KeyboardKey {
  ENTER = 'Enter',
}

/**
 * A hook which sets the focus to the first item in the list from the selected page on keyboard navigation when the page is changed.
 *
 * @param currentPage - the value of the selected page
 *
 * @returns a ref that gets attached to the list items container
 */
const usePaginationListFocus = (
  currentPage: number
): React.MutableRefObject<HTMLDivElement | null> => {
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const previousPage = useRef<number>(currentPage);
  const currentPageRef = useRef<number>(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const onEnter = (e: KeyboardEvent): void => {
      setTimeout(() => {
        if (e.key !== KeyboardKey.ENTER) {
          return;
        }

        if (currentPageRef.current === previousPage.current) {
          return;
        }

        const listContainer = paginationRef.current?.firstElementChild as HTMLElement;
        if (!listContainer) {
          return;
        }

        const firstListItem = listContainer.firstElementChild as HTMLElement;
        if (!firstListItem) {
          return;
        }

        const focusableItem = firstListItem.querySelector(
          'button, input, textarea, select, [href], [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        if (!focusableItem) {
          return;
        }
        focusableItem.focus();
        previousPage.current = currentPageRef.current;
        // Giving some time to the app to update the page in order to focus on the element
      }, 150);
    };

    const paginationList = paginationRef.current;
    if (!paginationList) {
      return;
    }

    paginationList.addEventListener('keydown', onEnter);
    return () => {
      paginationList.removeEventListener('keydown', onEnter);
    };
  }, [paginationRef]);
  return paginationRef;
};

export default usePaginationListFocus;
